import { fetchKeyValue, storeKeyValue } from "@/storage/indexeddb"
import toast from "react-hot-toast"
import {
	createPresetAtom,
	deletePresetAtomFamily,
	fetchPresetsAtom,
	updatePresetAtomFamily,
} from "@/store/presets/presets-state"
import { store } from "@/store/store"
import {
	AdjustmentKey,
	Adjustments,
	ColorProfileName,
	LookTable,
	LookTableEncoding,
	LookTableRGBGamma,
	LookTableRGBPrimaries,
	LookTableType,
	LookTableVersion,
	MaskGroup,
} from "@/core/types"
import { uuid } from "@/utils/uuid"
import { DefaultExcludedAdjustmentKeys } from "@/core/adjustments"
import equal from "fast-deep-equal"
import { hasExtension } from "@/storage/file-system"
import { convertCameraRawSetting, parseCameraRawSettings } from "@/core/utils/camera-raw-settings"
import { hashFileContent, hashJSONAdjustments, hashObject } from "@/utils/hash"
import { getCurrentAdjustmentsWithKeys } from "@/store/store-history"
import { base64ToString, stringToBase64 } from "@/utils/base64"
import { currentPhotoAtom } from "./photo/photos-state"
import { atom } from "jotai"
import { cached } from "@/utils/cached"
import { CreatePresetRequestDTO, ExportedPresetDTO, PresetContentType } from "@/api/dto/presets"
import { PNApiOperations } from "@/api/operations"
import { SignedUrlDTO } from "@/api/dto/upload"
import { PresetItem } from "@/store/presets/presets-service"
import { DataSource } from "@/store/data-source"
import { CubeLutParser } from "@/core/utils/cube-lut-parser"
import { chooseAndValidateDirectory } from "@/storage/open-directory"
import { LRTemplateFileContents, convertToXMP } from "../core/utils/presets-lrtemplate"
import {
	MainLUTResolver,
	serializeLookTable,
	deserializeLookTable,
} from "@/io/assets/lut/lut-resolver"
import { Sentry } from "@/telemetry/sentry"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer i18n
import i18n from "@/i18n"
import { replaceDialog } from "./dialogs-state"
import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import { clamp } from "@/core/utils/math"
import { getDecreasedValue } from "@/lib/ai/color-match/utils"
import { fetchWithNetworkBehavior } from "@/api/fetch-with-network-behavior"
import { getEditor } from "@/utils/useEditor"
import { openFilesWithPicker } from "@/utils/file"

export const nullPresetID = "null-preset-default-adjustments-unique-id"

export const DEFAULT_PRESET_GROUP = "User Presets"

export interface PresetAdjustmentsInfo {
	min: PresetAdjustmentsResult
	mid: PresetAdjustmentsResult
	max: PresetAdjustmentsResult
	photoId: string
	currentAdjustmentsDigest: string | null
}

export interface PresetAdjustmentsResult {
	presetId: string
	strength: number
	presetAdjustments: Adjustments
	finalAdjustments: Adjustments | null
}

export type PresetImportExtension = `.${string}`

interface LoadedPreset {
	adjustments: Partial<Adjustments>
	userLut?: LookTable
}

const stripUserLutAdjustments = (adjustments: Partial<Adjustments>): Partial<Adjustments> => {
	const stripped = { ...adjustments }
	delete stripped.userLutId
	delete stripped.userLutStrength
	delete stripped.userLutTitle
	return stripped
}

const normalizePresetLutAdjustments = (
	adjustments: Partial<Adjustments>,
	title?: string,
): Partial<Adjustments> => {
	const normalized = { ...adjustments }

	if (normalized.userLutId === undefined || normalized.userLutId === "") {
		normalized.userLutId = ""
		normalized.userLutTitle = ""
		normalized.userLutStrength = 1
		return normalized
	}

	normalized.userLutTitle = normalized.userLutTitle ?? title
	return normalized
}

export const getPresetIdWithStrength = (presetId: string, strength: number) =>
	`${presetId}&&strength=${strength}`

export const parsePresetIdAndStrength = (presetIdWithStrength: string) => {
	const [presetId, strength] = presetIdWithStrength.split("&&strength=")
	return { presetId, strength: strength ? parseFloat(strength) : 1 }
}

export async function exportPreset(preset: PresetItem) {
	const directory = await chooseAndValidateDirectory("export_preset")

	if (!(directory instanceof FileSystemDirectoryHandle)) {
		return
	}

	const handle = await directory.getFileHandle(`${preset.name}.preset`, {
		create: true,
	})

	const exportPreset = await getExportedPreset(preset)
	const data = JSON.stringify(exportPreset, null, 2)

	const writableStream = await handle.createWritable()
	await writableStream.write(data)
	await writableStream.close()

	toast.success(i18n.t("store:storePresets.presetExported", { name: preset.name }))
}

export function removePreset(preset: PresetItem) {
	const { mutate: deletePreset } = store.get(deletePresetAtomFamily(preset.id))
	deletePreset()
}

export function derivePresetWithUserAdjustmentsForAI(input: {
	userAdjustments: Adjustments
	presetLocalAdjustments: MaskGroup[]
	defaultAdjustments: Adjustments
	isLRParams?: boolean
}) {
	const { userAdjustments, presetLocalAdjustments, defaultAdjustments, isLRParams } = input

	let id = uuid()

	const adjustments = { ...userAdjustments }
	let keysToRemove = ["exposure", "temperature", "tint", "localAdjustments"]
	keysToRemove = keysToRemove.concat(DefaultExcludedAdjustmentKeys)
	// if (userAdjustments.saturation < 0 && userAdjustments.vibrance >= -0.24) {
	// 	keysToRemove.push("saturation") // saturation adjustment that is very likely from auto
	// }

	if (adjustments.sharpen === defaultAdjustments.sharpen || isLRParams) {
		keysToRemove.push("sharpen")
	}
	if (adjustments.colorNoiseReduction === defaultAdjustments.colorNoiseReduction || isLRParams) {
		keysToRemove.push("colorNoiseReduction")
	}

	keysToRemove.forEach((key) => {
		delete adjustments[key]
	})

	const keysToScaledown = ["highlights", "shadows", "contrast", "blacks", "whites", "saturation"]
	keysToScaledown.forEach((key) => {
		if (key === "saturation") {
			if (adjustments[key] !== undefined && adjustments[key] > -0.5) {
				adjustments[key] = clamp(getDecreasedValue(adjustments[key], 0.05, 0.4), -0.2, 0.2)
			}
		} else {
			if (adjustments[key] !== undefined) {
				adjustments[key] = clamp(getDecreasedValue(adjustments[key], 0.2, 0.5), -0.5, 0.5)
			}
		}
	})

	// clamp HSL sat and lum
	const hslHues = ["Red", "Orange", "Yellow", "Green", "Aqua", "Blue", "Purple", "Magenta"]
	let averageSat = 0
	let count = 0
	for (const hue of hslHues) {
		if (adjustments[`saturation${hue}`] !== undefined) {
			averageSat += adjustments[`saturation${hue}`]
			count++
		}
	}
	averageSat /= Math.max(1, count)
	const minSat = Math.min(-0.45, averageSat - 0.2)
	for (const hue of hslHues) {
		if (adjustments[`saturation${hue}`] !== undefined) {
			adjustments[`saturation${hue}`] = clamp(adjustments[`saturation${hue}`], minSat, 0.35)
		}
		if (adjustments[`luminance${hue}`] !== undefined) {
			adjustments[`luminance${hue}`] = clamp(adjustments[`luminance${hue}`], -0.3, 0.3)
		}
	}

	if (presetLocalAdjustments.length > 0) {
		adjustments.localAdjustments = presetLocalAdjustments
	}
	const currAdjustments = { ...defaultAdjustments, ...adjustments }
	if (equal(currAdjustments, defaultAdjustments)) {
		id = nullPresetID
	}

	return {
		id,
		adjustments,
	}
}

export const fetchPackagedPresets = () => {
	const packagedPresets: Record<string, string> = import.meta.glob("@assets/presets/**/*.xmp", {
		query: "?url",
		import: "default",
		eager: true,
	})

	const presets: PresetItem[] = []

	for (const modulePath of Object.keys(packagedPresets)) {
		/**
		 * Find public paths for all packaged presets
		 * e.g.
		 * url = "/public/presets/Airy/airy-1.xmp"
		 * parts = ['', 'public', 'presets', 'Airy', 'airy-1.xmp']
		 */
		const path = modulePath.split("/")
		const filename = path.at(-1)
		const folder = path.at(-2)
		const url = packagedPresets[modulePath]

		if (!filename || !folder || !url) {
			continue
		}

		const name = filename.split(".")[0] ?? "Preset"
		const date = new Date(0)
		const preset: PresetItem = {
			readOnly: true,
			createdAt: date,
			updatedAt: date,
			id: url,
			orderScore: date.getTime(),
			data: url,
			name,
			group: folder,
			schemaVersion: 1,
			contentType: PresetContentType.XMP,
			source: DataSource.IndexedDB,
		}

		presets.push(preset)
	}

	return presets
}

const packagedPresetsAtom = atom(fetchPackagedPresets())

export const userPresetsAtom = atom<{ data: PresetItem[]; isInitialLoading: boolean }>((get) => {
	const { data, isInitialLoading, isError } = get(fetchPresetsAtom)

	if (data && !isError) {
		return { data, isInitialLoading }
	}

	return {
		data: [],
		isInitialLoading,
	}
})

export const allPresetsAtom = atom<PresetItem[]>((get) => {
	const userPresets = get(userPresetsAtom).data
	const packagedPresets = get(packagedPresetsAtom)

	return userPresets.concat(packagedPresets)
})

export const loadingPresetsAtom = atom<Record<string, boolean>>({})

export const polarrPresetsAtom = atom((get) => {
	const presets = get(packagedPresetsAtom)
	const groupedPresets: Record<string, PresetItem[]> = {}

	for (const preset of presets) {
		if (preset.group) {
			groupedPresets[preset.group] = groupedPresets[preset.group] ?? []
			groupedPresets[preset.group]?.push(preset)
		}
	}

	for (const group of Object.keys(groupedPresets)) {
		groupedPresets[group] =
			groupedPresets[group]?.sort((a, b) => {
				// Extract number from the names by removing non-digit characters and sort
				const numA = parseInt(a.name.replace(/\D/g, ""))
				const numB = parseInt(b.name.replace(/\D/g, ""))
				return numA - numB
			}) ?? []
	}
	return groupedPresets
})

const loadPresetAdjustmentsFromXMPFile = async (
	presetFile: Blob,
	presetName: string,
): Promise<LoadedPreset> => {
	const data = await presetFile.text()
	const settings = parseCameraRawSettings(data)
	const { adjustments, userLut } = convertCameraRawSetting(settings)

	return {
		adjustments: normalizePresetLutAdjustments(
			{
				...adjustments,
				colorProfile: adjustments.colorProfile,
			},
			presetName,
		),
		userLut,
	}
}

const loadPresetAdjustmentsFromJSONFile = async (presetFile: Blob): Promise<LoadedPreset> => {
	const data = await presetFile.text()
	const preset = JSON.parse(data) as ExportedPresetDTO

	if (!preset) {
		throw new Error("Invalid preset file")
	}

	if (preset.schemaVersion === 1 && preset.content) {
		const adjustments = JSON.parse(preset.content) as Partial<Adjustments>

		// Check if preset includes LUT data
		let userLut: LookTable | undefined
		if (preset.lutData && adjustments.userLutId) {
			try {
				userLut = deserializeLookTableFromPreset(preset.lutData)
			} catch (error) {
				console.error("[preset] Failed to parse LUT data:", error)
			}
		}

		return {
			adjustments: normalizePresetLutAdjustments(adjustments, preset.name),
			userLut,
		}
	}

	throw new Error("No content in preset file")
}

const loadLookTableFromCUBEFile = async (presetFile: Blob): Promise<LookTable> => {
	const text = await presetFile.text()
	const cubeLut = CubeLutParser.parseLutFile(text, "cube")

	if (!cubeLut) {
		throw new Error("Cannot parse CUBE LUT file")
	}

	const cubeLutData = new Float32Array(cubeLut.data)

	const lookTable: LookTable = {
		id: uuid(),
		type: LookTableType.rgb,
		version: LookTableVersion.version1,
		hueDivisions: cubeLut.size,
		satDivisions: cubeLut.size,
		valDivisions: cubeLut.size,
		encoding: LookTableEncoding.sRGB,
		minAmount: 0.0,
		maxAmount: 1.0,
		data: cubeLutData,
		rgbPrimaries: LookTableRGBPrimaries.sRGB,
		rgbGamma: LookTableRGBGamma.sRGB,
	}

	return lookTable
}

const loadPresetAdjustmentsFromCUBEFile = async (
	presetFile: Blob,
	title: string,
): Promise<LoadedPreset> => {
	const lookTable = await loadLookTableFromCUBEFile(presetFile)

	return {
		adjustments: {
			userLutTitle: title,
			userLutId: lookTable.id,
			userLutStrength: 1,
		},
		userLut: lookTable,
	}
}

export const importUserLutFromCUBEFile = async (
	presetFile: Blob,
	title: string,
): Promise<Partial<Adjustments>> => {
	const lookTable = await loadLookTableFromCUBEFile(presetFile)
	await MainLUTResolver.add(lookTable)

	return {
		userLutTitle: title,
		userLutId: lookTable.id,
		userLutStrength: 1,
	}
}

export const loadPresetAdjustmentsFromUrl = async (
	url: string,
	title: string,
	type: string,
): Promise<LoadedPreset> => {
	const file = await fetchWithNetworkBehavior(url, undefined, {
		networkIntent: "active",
	}).then((res) => res.blob())
	if (type == PresetContentType.JSON) {
		const data = await file.text()
		const adjustments = JSON.parse(data) as Partial<Adjustments>

		// Migrating camera profile to color profile type
		if (typeof adjustments["cameraProfile"] === "string") {
			console.log(
				"Converting legacy cameraProfile",
				adjustments["cameraProfile"],
				"into new color profile",
			)

			switch (adjustments["cameraProfile"]) {
				case "RawColor":
					adjustments.colorProfile = ColorProfileName.Color
					break
				case "RawStandard":
					adjustments.colorProfile = ColorProfileName.Standard
					break
			}

			delete adjustments["cameraProfile"]
		}

		return {
			adjustments: normalizePresetLutAdjustments(adjustments, title),
		}
	} else if (type == PresetContentType.XMP) {
		return loadPresetAdjustmentsFromXMPFile(file, title)
	} else if (type == PresetContentType.CUBE) {
		return loadPresetAdjustmentsFromCUBEFile(file, title)
	} else {
		throw new Error("Unsupported preset content type to load adjustments from")
	}
}

export const storeLoadedPresetToIndexedDB = async (preset: PresetItem, result: LoadedPreset) => {
	await storeKeyValue(`preset-adjustments|${preset.id}`, result.adjustments)

	console.log(`[preset] Caching ${preset.contentType} preset`, preset.id)

	if (result.userLut) {
		console.log(`[preset] Caching ${preset.contentType} preset`, preset.id)
		await cachePresetLut(preset.id, result.userLut)
	}
}

export const loadPresetAdjustments = cached(
	async (preset: PresetItem): Promise<Partial<Adjustments>> => {
		console.log("[preset] Loading adjustments for", preset)

		if (preset.adjustments) {
			console.log("[preset] Adjustments are already on preset")
			return normalizePresetLutAdjustments(preset.adjustments, preset.name)
		}

		const indexedDbCacheAdjustmentsEntry = await fetchKeyValue(
			`preset-adjustments|${preset.id}`,
		)

		if (indexedDbCacheAdjustmentsEntry) {
			const cachedAdjustments = normalizePresetLutAdjustments(
				indexedDbCacheAdjustmentsEntry.value as Partial<Adjustments>,
				preset.name,
			)

			if (cachedAdjustments.userLutId) {
				// manually add userLutTitle to cached adjustments
				if (!cachedAdjustments.userLutTitle) {
					cachedAdjustments.userLutTitle = preset.name
				}
				const indexedDbCacheLutEntry = await fetchKeyValue(
					`preset-adjustments-lut|${preset.id}`,
				)

				if (indexedDbCacheLutEntry?.value) {
					await MainLUTResolver.add(indexedDbCacheLutEntry.value as LookTable)
					console.log("[preset] Cache adjustments + LUT found")
					return cachedAdjustments
				} else {
					console.log(
						"[preset] Cached adjustments discarded because associated LUT not found",
					)
				}
			} else {
				console.log("[preset] Cached adjustments found")
				return cachedAdjustments
			}
		}

		if (!preset.schemaVersion || preset.schemaVersion === 1) {
			if (preset.data instanceof File) {
				if (hasExtension(preset.data, [".xmp"])) {
					const result = await loadPresetAdjustmentsFromXMPFile(preset.data, preset.name)
					await storeLoadedPresetToIndexedDB(preset, result)
					return result.adjustments
				} else if (hasExtension(preset.data, [".cube"])) {
					const result = await loadPresetAdjustmentsFromCUBEFile(preset.data, preset.name)
					await storeLoadedPresetToIndexedDB(preset, result)
					return result.adjustments
				} else {
					const result = await loadPresetAdjustmentsFromJSONFile(preset.data)
					await storeLoadedPresetToIndexedDB(preset, result)
					return result.adjustments
				}
			} else if (typeof preset.data === "string") {
				const result = await loadPresetAdjustmentsFromUrl(
					preset.data,
					preset.name,
					PresetContentType.XMP,
				)

				await storeLoadedPresetToIndexedDB(preset, result)

				return result.adjustments
			}
		} else if (preset.schemaVersion === 2 && preset.contentUrl && preset.contentType) {
			const result = await loadPresetAdjustmentsFromUrl(
				preset.contentUrl,
				preset.name,
				preset.contentType,
			)

			// For JSON presets with userLutId, try to load LUT from IndexedDB or server
			if (
				preset.contentType === PresetContentType.JSON &&
				result.adjustments.userLutId &&
				!result.userLut
			) {
				const cachedLutEntry = await fetchKeyValue(`preset-adjustments-lut|${preset.id}`)
				if (cachedLutEntry?.value) {
					result.userLut = cachedLutEntry.value as LookTable
				} else if (preset.lutUrl) {
					result.userLut = await loadPresetLutFromUrl(preset)
				}
			}

			await storeLoadedPresetToIndexedDB(preset, result)
			return result.adjustments
		}
		return {}
	},
	(preset) => preset.id,
)

const importLRTemplatePreset = async (lrtFile: File, group?: string) => {
	const createPreset = store.get(createPresetAtom).mutateAsync as (
		preset: CreatePresetRequestDTO,
	) => Promise<PresetItem>

	const contents = (await lrtFile.text()) as LRTemplateFileContents
	const xmp = convertToXMP(contents)
	const settings = parseCameraRawSettings(xmp)
	const xmpFile = new File([xmp], lrtFile.name.replace(".lrtemplate", ".xmp"), {
		type: "application/xhtml+xml",
	})

	const contentDigest = await hashFileContent(xmpFile)
	const existingPreset = findExistingPreset(contentDigest)
	if (!existingPreset) {
		return createPreset({
			name: (settings.Name as string) ?? xmpFile.name.split(".")[0],
			schemaVersion: 2,
			contentUrl: await contentBlobToContentUrl(
				xmpFile,
				PresetContentType.XMP,
				contentDigest,
			),
			contentType: PresetContentType.XMP,
			contentDigest,
			group: group ?? DEFAULT_PRESET_GROUP,
		})
	} else {
		return existingPreset
	}
}

const importXMPPreset = async (file: File, group?: string) => {
	const createPreset = store.get(createPresetAtom).mutateAsync as (
		preset: CreatePresetRequestDTO,
	) => Promise<PresetItem>
	const contents = await file.text()
	const settings = parseCameraRawSettings(contents)
	const contentDigest = await hashFileContent(file)

	const existingPreset = findExistingPreset(contentDigest)
	if (!existingPreset) {
		return createPreset({
			name: (settings.Name as string) ?? file.name.split(".")[0],
			schemaVersion: 2,
			contentUrl: await contentBlobToContentUrl(file, PresetContentType.XMP, contentDigest),
			contentType: PresetContentType.XMP,
			contentDigest,
			group: group ?? DEFAULT_PRESET_GROUP,
		})
	} else {
		return existingPreset
	}
}

const importCubePreset = async (file: File, group?: string) => {
	const createPreset = store.get(createPresetAtom).mutateAsync as (
		preset: CreatePresetRequestDTO,
	) => Promise<PresetItem>

	const contentDigest = await hashFileContent(file)
	const existingPreset = findExistingPreset(contentDigest)
	if (!existingPreset) {
		return createPreset({
			name: file.name.split(".")[0] ?? "Untitled Preset",
			schemaVersion: 2,
			contentUrl: await contentBlobToContentUrl(file, PresetContentType.CUBE, contentDigest),
			contentType: PresetContentType.CUBE,
			contentDigest,
			group: group ?? DEFAULT_PRESET_GROUP,
		})
	} else {
		return existingPreset
	}
}

function findExistingPreset(contentDigest: string, id?: string) {
	const existingPreset = store
		.get(allPresetsAtom)
		.find((item) => item.id === id || item.contentDigest === contentDigest)
	return existingPreset
}

export async function importPresetFromHandle(
	handle: FileSystemFileHandle | File,
	group?: string,
): Promise<PresetItem | undefined> {
	const file = handle instanceof File ? handle : await handle.getFile()
	const text = await file.text()

	if (hasExtension(handle, [".lrtemplate"])) {
		return importLRTemplatePreset(file, group)
	}

	if (hasExtension(handle, [".xmp"])) {
		return importXMPPreset(file, group)
	}

	if (hasExtension(handle, [".cube"])) {
		return importCubePreset(file, group)
	}

	if (hasExtension(handle, [".preset"])) {
		const preset = (await JSON.parse(text)) as ExportedPresetDTO
		const existingPreset = findExistingPreset(preset.contentDigest, preset.id)
		if (!existingPreset) {
			let presetContent = preset.content
			let importedUserLut: LookTable | undefined
			let droppedInvalidLutData = false
			if (preset.contentType === PresetContentType.JSON && preset.content) {
				const adjustments = JSON.parse(preset.content) as Partial<Adjustments>
				const normalizedAdjustments = normalizePresetLutAdjustments(
					adjustments,
					preset.name,
				)
				presetContent = JSON.stringify(normalizedAdjustments)
				if (preset.lutData && adjustments.userLutId) {
					try {
						importedUserLut = deserializeLookTableFromPreset(preset.lutData)
					} catch (error) {
						droppedInvalidLutData = true
						Sentry.captureException(error)
						console.warn("[preset] Failed to restore LUT from preset import", error)
						presetContent = JSON.stringify(
							stripUserLutAdjustments(normalizedAdjustments),
						)
					}
				}
			}

			let contentUrl: string
			let contentDigest: string
			let lutUrl: string | undefined
			if ((!preset.schemaVersion || preset.schemaVersion === 1) && preset.content) {
				if (preset.contentType === PresetContentType.JSON) {
					const contentBlob = new Blob([presetContent], { type: "application/json" })
					contentDigest = await hashJSONAdjustments(contentBlob)
					contentUrl = await contentBlobToContentUrl(
						contentBlob,
						PresetContentType.JSON,
						contentDigest,
					)
				} else if (preset.contentType === PresetContentType.XMP) {
					const contentBlob = new Blob([base64ToString(preset.content)], {
						type: "text/plain",
					})
					contentDigest = await hashFileContent(contentBlob)
					contentUrl = await contentBlobToContentUrl(
						contentBlob,
						PresetContentType.XMP,
						contentDigest,
					)
				} else {
					throw new Error(
						`Detected unsupported preset content type: ${preset.contentType}`,
					)
				}
			} else {
				throw new Error(`Incompatible schema version detected: ${preset.schemaVersion}`)
			}
			const createPreset = store.get(createPresetAtom).mutateAsync as (
				preset: CreatePresetRequestDTO,
			) => Promise<PresetItem>
			if (importedUserLut) {
				lutUrl = await uploadPresetLut(importedUserLut)
			}
			const presetItem = await createPreset({
				presetId: preset.id,
				name: preset.name ?? file.name.split(".")[0],
				schemaVersion: 2,
				contentUrl,
				contentType: preset.contentType,
				contentDigest,
				group: group ?? DEFAULT_PRESET_GROUP,
				lutUrl,
			})

			if (importedUserLut) {
				await cachePresetLut(presetItem.id, importedUserLut)
			} else if (droppedInvalidLutData) {
				toast.error(i18n.t("store:storePresets.presetImportLutError"))
			}

			return presetItem
		} else {
			return existingPreset
		}
	}
}

const buildTemporaryPreset = async (
	file: File,
	result: LoadedPreset,
	options: {
		contentDigest: string
		contentType?: PresetContentType
		name: string
	},
) => {
	const createdAt = new Date()
	const preset: PresetItem = {
		id: `style-creation-preset-${options.contentDigest}`,
		createdAt,
		updatedAt: createdAt,
		name: options.name,
		orderScore: createdAt.getTime() / 1000,
		schemaVersion: 2,
		data: file,
		adjustments: result.adjustments,
		contentType: options.contentType,
		contentDigest: options.contentDigest,
		source: DataSource.IndexedDB,
		readOnly: true,
	}

	await storeLoadedPresetToIndexedDB(preset, result)
	return preset
}

const loadStyleCreationPresetFromHandle = async (
	handle: FileSystemFileHandle | File,
): Promise<PresetItem | undefined> => {
	const file = handle instanceof File ? handle : await handle.getFile()
	const title = file.name.split(".")[0] ?? "Untitled Preset"
	let loadedPreset: LoadedPreset
	let contentDigest: string
	let contentType: PresetContentType | undefined
	let name = title

	if (hasExtension(handle, [".lrtemplate"])) {
		const contents = (await file.text()) as LRTemplateFileContents
		const xmp = convertToXMP(contents)
		const settings = parseCameraRawSettings(xmp)
		const xmpFile = new File([xmp], file.name.replace(".lrtemplate", ".xmp"), {
			type: "application/xhtml+xml",
		})

		loadedPreset = await loadPresetAdjustmentsFromXMPFile(xmpFile, title)
		contentDigest = await hashFileContent(xmpFile)
		contentType = PresetContentType.XMP
		name = (settings.Name as string) ?? title
	} else if (hasExtension(handle, [".xmp"])) {
		const contents = await file.text()
		const settings = parseCameraRawSettings(contents)

		loadedPreset = await loadPresetAdjustmentsFromXMPFile(file, title)
		contentDigest = await hashFileContent(file)
		contentType = PresetContentType.XMP
		name = (settings.Name as string) ?? title
	} else if (hasExtension(handle, [".cube"])) {
		loadedPreset = await loadPresetAdjustmentsFromCUBEFile(file, title)
		contentDigest = await hashFileContent(file)
		contentType = PresetContentType.CUBE
	} else if (hasExtension(handle, [".preset"])) {
		const preset = JSON.parse(await file.text()) as ExportedPresetDTO
		name = preset.name ?? title
		contentType = preset.contentType

		if (preset.contentType === PresetContentType.XMP) {
			const contentBlob = new Blob([base64ToString(preset.content)], {
				type: "text/plain",
			})
			loadedPreset = await loadPresetAdjustmentsFromXMPFile(contentBlob, name)
			contentDigest = preset.contentDigest ?? (await hashFileContent(contentBlob))
		} else {
			loadedPreset = await loadPresetAdjustmentsFromJSONFile(file)
			contentDigest = preset.contentDigest ?? hashObject(loadedPreset.adjustments)
		}
	} else {
		throw new Error("Unsupported preset content type to load adjustments from")
	}

	return buildTemporaryPreset(file, loadedPreset, {
		contentDigest,
		contentType,
		name,
	})
}

export async function importStyleCreationPreset(): Promise<PresetImportResult> {
	const { handles } = await selectPresetHandles(
		{
			selectFolder: false,
			selectMultiple: false,
		},
		STYLE_CREATION_IMPORTABLE_PRESET_EXTENSIONS,
	)

	if (handles.length === 0) {
		return { group: undefined, errors: {}, successCount: 0, presets: [] }
	}

	return importPresetsFromHandles(handles, undefined, loadStyleCreationPresetFromHandle)
}

export type PresetImportResult = {
	errors: Record<string, Error>
	group: string | undefined
	successCount: number
	presets: PresetItem[]
}

export const DEFAULT_IMPORTABLE_PRESET_EXTENSIONS: PresetImportExtension[] = [".preset"]
export const LIGHTROOM_IMPORTABLE_PRESET_EXTENSIONS: PresetImportExtension[] = [
	".xmp",
	".lrtemplate",
]
const STYLE_CREATION_IMPORTABLE_PRESET_EXTENSIONS: PresetImportExtension[] = [
	...LIGHTROOM_IMPORTABLE_PRESET_EXTENSIONS,
	...DEFAULT_IMPORTABLE_PRESET_EXTENSIONS,
]

async function selectPresetHandles(
	props: {
		selectFolder: boolean
		selectMultiple: boolean
	},
	allowedExtensions: PresetImportExtension[],
) {
	let handles: (FileSystemFileHandle | File)[] = []
	let group: string | undefined
	if (props.selectFolder) {
		const directory = await window.showDirectoryPicker()
		group = directory.name

		for await (const handle of directory.values()) {
			if (handle.kind === "file" && hasExtension(handle, allowedExtensions)) {
				handles.push(handle)
			}
		}
	} else {
		const pickerOptions: OpenFilePickerOptions = {
			types: [
				{
					description: "Presets", // i18n-ignore — browser File Picker API
					accept: {
						"application/xhtml+xml": allowedExtensions,
					},
				},
			],
			excludeAcceptAllOption: true,
			multiple: props.selectMultiple,
		}
		handles = await openFilesWithPicker(pickerOptions)
	}

	return { group, handles }
}

async function importPresetsFromHandles(
	handles: (FileSystemFileHandle | File)[],
	group?: string,
	loadHandle: (
		handle: FileSystemFileHandle | File,
		group?: string,
	) => Promise<PresetItem | undefined> = importPresetFromHandle,
): Promise<PresetImportResult> {
	const result: PresetImportResult = await Promise.allSettled(
		handles.map((handle) => loadHandle(handle, group)),
	).then((results) => {
		const errorsMap: Record<string, Error> = {}
		const presets: PresetItem[] = []
		results.map((r, index) => {
			const handle = handles[index]
			if (handle && r.status === "rejected") {
				const error = r.reason as Error
				errorsMap[handle.name] = error
				Sentry.captureException(error)
			} else if (handle && r.status === "fulfilled") {
				presets.push(r.value as PresetItem)
			}
		})
		return {
			group,
			errors: errorsMap,
			successCount: results.filter((r) => r.status === "fulfilled").length,
			presets: presets,
		}
	})
	return result
}

export async function importPreset(props: {
	selectFolder: boolean
	selectMultiple: boolean
	allowedExtensions?: PresetImportExtension[]
}) {
	const { group, handles } = await selectPresetHandles(
		props,
		props.allowedExtensions ?? DEFAULT_IMPORTABLE_PRESET_EXTENSIONS,
	)

	if (handles.length === 0) {
		return { group: undefined, errors: {}, successCount: 0, presets: [] }
	}

	return importPresetsFromHandles(handles, group)
}

async function contentBlobToContentUrl(
	contentBlob: Blob,
	contentType: PresetContentType,
	contentDigest: string,
) {
	const [uploadUrl] = await PNApiOperations.getSignedUrls([
		{
			contentType,
			entityType: "preset-content",
			contentDigest,
		},
	])

	if (!uploadUrl) {
		throw new Error("Couldn't get upload url")
	}

	return resolveContentUrlFromUploadUrl(uploadUrl, contentBlob)
}

const deserializeLookTableFromPreset = (lutData: string): LookTable => {
	const serializableLut = JSON.parse(base64ToString(lutData)) as Omit<LookTable, "data"> & {
		data: number[]
	}
	return {
		...serializableLut,
		data: new Float32Array(serializableLut.data),
	}
}

const uploadPresetLut = async (lookTable: LookTable): Promise<string> => {
	const lutBlob = serializeLookTable(lookTable)
	const lutContentDigest = await hashFileContent(lutBlob)
	const [uploadUrl] = await PNApiOperations.getSignedUrls([
		{
			contentType: "octet-stream",
			entityType: "preset-lut",
			contentDigest: lutContentDigest,
		},
	])

	if (!uploadUrl) {
		throw new Error("Couldn't get preset LUT upload url")
	}

	if (uploadUrl.alreadyExists) {
		return uploadUrl.url
	}

	const { url } = await PNApiOperations.uploadContent({
		contentType: lutBlob.type || "application/octet-stream",
		data: lutBlob,
		url: uploadUrl.url,
	})

	if (!url) {
		throw new Error("Failed to upload preset LUT")
	}

	return url
}

const cachePresetLut = async (presetId: string, lookTable: LookTable) => {
	await storeKeyValue(`preset-adjustments-lut|${presetId}`, lookTable)
	await MainLUTResolver.add(lookTable)
}

const loadPresetLutFromUrl = async (preset: Pick<PresetItem, "id" | "lutUrl">) => {
	if (!preset.lutUrl) {
		return
	}

	try {
		const response = await fetch(preset.lutUrl)
		if (!response.ok) {
			throw new Error(`Failed to fetch preset LUT: ${response.status} ${response.statusText}`)
		}

		return deserializeLookTable(await response.arrayBuffer())
	} catch (error) {
		Sentry.captureException(error)
		console.warn("[preset] Failed to load LUT for preset", preset.id, error)
	}
}

const resolveContentUrlFromUploadUrl = async (
	uploadUrl: SignedUrlDTO,
	presetBlob: Blob,
): Promise<string> => {
	if (uploadUrl.alreadyExists) {
		return uploadUrl.url
	}
	const { url: contentUrl } = await PNApiOperations.uploadContent({
		contentType: "application/x-www-form-urlencoded",
		data: presetBlob,
		url: uploadUrl.url,
	})
	return contentUrl
}

const getExportedPreset = async (record: PresetItem): Promise<ExportedPresetDTO> => {
	const adjustments = normalizePresetLutAdjustments(
		await loadPresetAdjustments(record),
		record.name,
	)
	const exportedPreset: ExportedPresetDTO = {
		id: record.id,
		name: record.name,
		createdAt: record.createdAt,
		updatedAt: record.updatedAt,
		group: record.group,
		schemaVersion: 1,
		contentType: PresetContentType.JSON,
		contentDigest: hashObject(adjustments),
		orderScore: record.createdAt.getTime(),
		content: JSON.stringify(adjustments),
	}
	if (record.contentType === PresetContentType.XMP && record.contentUrl) {
		exportedPreset.contentType = PresetContentType.XMP
		const contentBlob = await fetchWithNetworkBehavior(record.contentUrl, undefined, {
			networkIntent: "active",
		}).then((res) => res.blob())
		exportedPreset.content = stringToBase64(await contentBlob.text())
		exportedPreset.contentDigest = await hashFileContent(contentBlob)
	}

	// Include LUT data if adjustments contain userLutId
	if (adjustments.userLutId) {
		const cachedLutEntry = await fetchKeyValue(`preset-adjustments-lut|${record.id}`)
		if (cachedLutEntry?.value) {
			const lookTable = cachedLutEntry.value as LookTable
			// Serialize LUT data: convert Float32Array to regular array for JSON serialization
			const serializableLut = {
				...lookTable,
				data: Array.from(lookTable.data),
			}
			exportedPreset.lutData = stringToBase64(JSON.stringify(serializableLut))
		}
	}

	return exportedPreset
}

export async function createPresetFromCurrentAdjustments(
	keysSelection: Set<AdjustmentKey>,
	name: string,
) {
	const selectedAdjustmentFields = getCurrentAdjustmentsWithKeys(
		keysSelection,
	) as Partial<Adjustments>
	const selectedAdjustments = normalizePresetLutAdjustments(selectedAdjustmentFields, name)
	const { mutateAsync: createPreset } = store.get(createPresetAtom)
	const contentBlob = new Blob([JSON.stringify(selectedAdjustments)], {
		type: "application/json",
	})
	const contentDigest = await hashJSONAdjustments(contentBlob)
	const contentUrl = await contentBlobToContentUrl(
		contentBlob,
		PresetContentType.JSON,
		contentDigest,
	)

	// Upload LUT data if preset includes userLutId
	let lutUrl: string | undefined
	let userLut: LookTable | undefined
	if (selectedAdjustments.userLutId) {
		const editor = getEditor()
		userLut = editor.renderer.assets.getArtefact({
			type: "lut",
			id: selectedAdjustments.userLutId,
		}) as LookTable | undefined

		if (userLut) {
			lutUrl = await uploadPresetLut(userLut)
		}
	}

	const presetItem = await createPreset({
		name,
		schemaVersion: 2,
		contentUrl,
		contentType: PresetContentType.JSON,
		contentDigest,
		group: DEFAULT_PRESET_GROUP,
		lutUrl,
	})

	// If adjustments include a LUT, cache it for this preset
	if (userLut) {
		await cachePresetLut(presetItem.id, userLut)
	}
}

export const renamePreset = (preset: PresetItem, name: string) => {
	const { mutate: updatePreset } = store.get(updatePresetAtomFamily(preset.id))
	updatePreset({
		name,
	})
}

export const updatePresetGroup = (preset: PresetItem, group: string) => {
	const { mutate: updatePreset } = store.get(updatePresetAtomFamily(preset.id))
	updatePreset({
		group,
	})
}

export const renamePresetGroup = (name: string, presets: PresetItem[]) => {
	for (const preset of presets) {
		updatePresetGroup(preset, name)
	}
}

export const removePresetGroup = (presets: PresetItem[], group: string) => {
	if (presets.length === 0) {
		return
	}

	replaceDialog(ConfirmDialog, {
		trackingViewName: "remove_preset_group",
		title: i18n.t("store:storePresets.removePresetGroupTitle", { group }),
		message: i18n.t("store:storePresets.removePresetGroupMessage"),
		primary: {
			title: i18n.t("common:action.delete"),
			isDestructive: true,
			handler: () => {
				for (const preset of presets) {
					removePreset(preset)
				}
			},
		},
		secondary: { title: i18n.t("common:action.cancel") },
	})
}

export const currentPresetAtom = atom((get) => {
	const presetAggregatedId = get(currentPhotoAtom)?.presetId
	if (!presetAggregatedId) {
		return null
	}

	const { presetId, strength } = parsePresetIdAndStrength(presetAggregatedId)

	const presets = get(allPresetsAtom)
	const preset = presets.find((p) => p.id === presetId)

	return { preset, strength }
})

export const currentPresetAdjustmentsAtom = atom<PresetAdjustmentsInfo | null>(null)
